<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import type { DiffRow } from '../../types/TextDiffsTypes';

const props = defineProps({
    diffData: {
        type: Array as () => DiffRow[],
        required: true,
    },
    showRenderedView: {
        type: Boolean,
        required: true,
    },
});

const { t } = useI18n();

function getRowClass(rowData: DiffRow): string {
    if (rowData.isOnlyInStaging) {
        return 'diff-row-added';
    } else if (rowData.isOnlyInProductive) {
        return 'diff-row-removed';
    } else if (rowData.isDifferent) {
        return 'diff-row-changed';
    }
    return 'diff-row-unchanged';
}

function getValueClass(rowData: DiffRow): string {
    if (rowData.isOnlyInStaging) {
        return 'value-added';
    } else if (rowData.isOnlyInProductive) {
        return 'value-removed';
    } else if (rowData.isDifferent) {
        return 'value-changed';
    }
    return '';
}

function getDisplayValue(value: string): string {
    if (props.showRenderedView) {
        // In rendered mode, return the HTML as-is for v-html rendering
        return value;
    }
    
    // In HTML mode, return the raw string (this will show the tags)
    return value;
}

function shouldUseHtml(): boolean {
    return props.showRenderedView;
}
</script>

<template>
    <DataTable
        :value="diffData"
        :row-class="getRowClass"
        class="diff-table"
        data-cy="text-diffs_table"
    >
        <Column field="key" :header="t('textDiffs.key')" class="key-column" :sortable="true" data-cy="text-diffs_key-column">
            <template #body="slotProps">
                <span class="key-text" data-cy="text-diffs_key-text">{{ slotProps.data.key }}</span>
            </template>
        </Column>
        
        <Column :header="t('textDiffs.staging')" class="staging-column" data-cy="text-diffs_staging-column">
            <template #body="slotProps">
                <div class="value-cell" data-cy="text-diffs_staging-value">
                    <span 
                        v-if="slotProps.data.isOnlyInProductive"
                        class="missing-value"
                        data-cy="text-diffs_staging-missing"
                    >
                        {{ t('textDiffs.missing') }}
                    </span>
                    <span 
                        v-else-if="shouldUseHtml()"
                        class="value-text"
                        :class="getValueClass(slotProps.data)"
                        v-html="getDisplayValue(slotProps.data.stagingValue)"
                        data-cy="text-diffs_staging-html-value"
                    >
                    </span>
                    <span 
                        v-else
                        class="value-text"
                        :class="getValueClass(slotProps.data)"
                        data-cy="text-diffs_staging-text-value"
                    >
                        {{ getDisplayValue(slotProps.data.stagingValue) }}
                    </span>
                </div>
            </template>
        </Column>
        
        <Column :header="t('textDiffs.productive')" class="productive-column" data-cy="text-diffs_productive-column">
            <template #body="slotProps">
                <div class="value-cell" data-cy="text-diffs_productive-value">
                    <span 
                        v-if="slotProps.data.isOnlyInStaging"
                        class="missing-value"
                        data-cy="text-diffs_productive-missing"
                    >
                        {{ t('textDiffs.missing') }}
                    </span>
                    <span 
                        v-else-if="shouldUseHtml()"
                        class="value-text"
                        :class="getValueClass(slotProps.data)"
                        v-html="getDisplayValue(slotProps.data.productiveValue)"
                        data-cy="text-diffs_productive-html-value"
                    >
                    </span>
                    <span 
                        v-else
                        class="value-text"
                        :class="getValueClass(slotProps.data)"
                        data-cy="text-diffs_productive-text-value"
                    >
                        {{ getDisplayValue(slotProps.data.productiveValue) }}
                    </span>
                </div>
            </template>
        </Column>
    </DataTable>
</template>

<style scoped>
.key-text {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: var(--p-primary-color);
}

.value-changed {
    background-color: var(--diff-changed-text-bg);
    color: var(--diff-changed-text);
    padding: 0.125rem 0.25rem;
    border-radius: 3px;
}

.value-added {
    background-color: var(--diff-added-text-bg);
    color: var(--diff-added-text);
    padding: 0.125rem 0.25rem;
    border-radius: 3px;
}

.value-removed {
    background-color: var(--diff-removed-text-bg);
    color: var(--diff-removed-text);
    padding: 0.125rem 0.25rem;
    border-radius: 3px;
}

.missing-value {
    font-style: italic;
    color: var(--p-text-muted-color);
    opacity: 0.7;
}

.value-text {
    white-space: pre-wrap;
    word-break: break-word;
}

/* Row styling for different states */
:deep(.diff-row-unchanged) {
    background-color: transparent !important;
}
:deep(.diff-row-changed) {
    background-color: var(--diff-changed-bg) !important;
}

:deep(.diff-row-added) {
    background-color: var(--diff-added-bg) !important;
}

:deep(.diff-row-removed) {
    background-color: var(--diff-removed-bg) !important;
}

:deep(.diff-row-unchanged:hover) {
    background-color: var(--diff-unchanged-hover) !important;
}

:deep(.diff-row-changed:hover) {
    background-color: var(--diff-changed-hover) !important;
    --diff-changed-text-bg: var(--diff-changed-hover-bg);
}

:deep(.diff-row-added:hover) {
    background-color: var(--diff-added-hover) !important;
    --diff-added-text-bg: var(--diff-added-hover-bg);
    --diff-added-text: #f6f5f5;
}

:deep(.diff-row-removed:hover) {
    background-color: var(--diff-removed-hover) !important;
    --diff-removed-text-bg: var(--diff-removed-hover-bg);
    --diff-removed-text: #f6f5f5;
}
</style>
